import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, NavLink, Navigate, useNavigate } from 'react-router-dom';
import io from 'socket.io-client';
import { App as CapApp } from '@capacitor/app';
import { StatusBar, Style } from '@capacitor/status-bar';
import { NotificationDto, notificationSocketEvents } from './notificationContracts';
import Dashboard from './pages/Dashboard';
import Login from './pages/Login';
import Requisicoes from './pages/Requisicoes';
import Aprovacoes from './pages/Aprovacoes';
import Comprador from './pages/Comprador';
import Armazem from './pages/Armazem';
import FornecedorPortal from './pages/FornecedorPortal';
import AdminGlobal from './pages/AdminGlobal';
import EquipaInterna from './pages/EquipaInterna';
import SubscricaoEmpresa from './pages/SubscricaoEmpresa';
import Fornecedores from './pages/Fornecedores';
import FeedbackPlataforma from './pages/FeedbackPlataforma';
import ResetPassword from './pages/ResetPassword';
import PerfilOperacional from './pages/PerfilOperacional';
import Comunicacoes from './pages/Comunicacoes';
import AdminComunicados from './pages/AdminComunicados';
import Sobre from './pages/Sobre';
import PrivacyPolicy from './pages/PrivacyPolicy';
import DeleteAccount from './pages/DeleteAccount';
import { APP_VERSION } from './content/sobre';
import AlertaPerfilIncompleto from './components/AlertaPerfilIncompleto';
import api, { limparSessao, SessaoTipo, storageKeys, getToken } from './services/api';
import { Operacao, perfilAdministrativo, perfilPode, carregarUsuarioSessao } from './permissions';
import { DialogProvider, useDialog } from './components/common/DialogContext';
import { setDialogInstance } from './services/dialog';
import { SOCKET_BASE_URL } from './config/env';
import { storage } from './services/storage';
import './styles/global.css';
import './App.css';

type Notificacao = NotificationDto & {
  id: string;
  tipo: string;
  categoria?: string;
  titulo?: string;
  mensagem: string;
  status?: string;
  entidadeId?: string;
  requisicaoId?: string;
  urlDestino?: string;
  lida?: boolean;
  createdAt: string;
};

const DialogInitializer: React.FC = () => {
  const dialog = useDialog();
  useEffect(() => {
    setDialogInstance(dialog);
  }, [dialog]);
  return null;
};

const AppContent: React.FC = () => {
  const [autenticado, setAutenticado] = useState(false);
  const [usuario, setUsuario] = useState<any>(null);
  const [tipoSessao, setTipoSessao] = useState<SessaoTipo>('usuario');
  const [carregando, setCarregando] = useState(true);
  const [notificacoes, setNotificacoes] = useState<Notificacao[]>([]);
  const [notificacoesAbertas, setNotificacoesAbertas] = useState(false);
  const [carregandoNotificacoes, setCarregandoNotificacoes] = useState(false);
  const [erroNotificacoes, setErroNotificacoes] = useState<string | null>(null);
  const [paginaNotificacoes, setPaginaNotificacoes] = useState(1);
  const [totalPaginasNotificacoes, setTotalPaginasNotificacoes] = useState(1);
  const [naoLidas, setNaoLidas] = useState(0);
  const navigate = useNavigate();

  // Configuração de UI Nativa (Status Bar)
  useEffect(() => {
    const setupNativeUI = async () => {
      try {
        if (!!(window as any).Capacitor) {
          await StatusBar.setStyle({ style: Style.Dark });
          await StatusBar.setBackgroundColor({ color: '#000000' });
        }
      } catch (err) {
        console.warn('[NativeUI] Falha ao configurar StatusBar', err);
      }
    };
    setupNativeUI();
  }, []);

  // Handler para o botão Voltar do Android
  useEffect(() => {
    let handler: any;

    const setupBackButton = async () => {
      if (!(window as any).Capacitor) return;

      handler = await CapApp.addListener('backButton', ({ canGoBack }) => {
        if (notificacoesAbertas) {
          setNotificacoesAbertas(false);
          return;
        }

        if (canGoBack) {
          window.history.back();
        } else {
          CapApp.exitApp();
        }
      });
    };

    setupBackButton();

    return () => {
      if (handler) handler.remove();
    };
  }, [notificacoesAbertas]);

  const endpointNotificacoes = tipoSessao === 'fornecedor' ? '/fornecedores/notificacoes' : '/notificacoes';

  const chaveNotificacao = (notificacao: Partial<Notificacao>) => notificacao.id;

  const normalizarNotificacao = (dados: any): Notificacao => ({
    id: dados?.id || '',
    tipo: dados?.tipo || 'NOTIFICACAO',
    categoria: dados?.categoria,
    titulo: dados?.titulo,
    mensagem: dados?.mensagem || 'Nova atualização recebida.',
    status: dados?.status,
    entidadeId: dados?.entidadeId || dados?.entidade_id || dados?.requisicaoId || dados?.requisicao_id,
    requisicaoId: dados?.requisicaoId || dados?.requisicao_id,
    urlDestino: dados?.urlDestino || dados?.url_destino,
    lida: Boolean(dados?.lida),
    createdAt: dados?.createdAt || dados?.created_at || new Date().toISOString()
  });

  const carregarNotificacoes = useCallback(async (pagina = 1) => {
    if (!autenticado) {
      return;
    }

    setCarregandoNotificacoes(true);
    setErroNotificacoes(null);

    try {
      const resposta = await api.get(endpointNotificacoes, {
        params: { pagina, limite: 8 }
      });
      const itens = Array.isArray(resposta.data?.itens)
        ? resposta.data.itens.map(normalizarNotificacao)
        : [];
      setNotificacoes(itens);
      setNaoLidas(Number(resposta.data?.naoLidas || 0));
      setPaginaNotificacoes(Number(resposta.data?.paginacao?.pagina || pagina));
      setTotalPaginasNotificacoes(Math.max(1, Number(resposta.data?.paginacao?.paginas || 1)));
    } catch (erro) {
      console.warn('Não foi possível carregar notificações persistentes.', erro);
      setErroNotificacoes('Não foi possível carregar as notificações.');
    } finally {
      setCarregandoNotificacoes(false);
    }
  }, [autenticado, endpointNotificacoes]);

  const adicionarNotificacao = useCallback((dados: any) => {
    if (!dados?.id) {
      console.warn('Notificação em tempo real ignorada por não possuir ID persistido.');
      return;
    }

    if (dados?.destinatarioPerfil && dados.destinatarioPerfil !== usuario?.perfil) {
      return;
    }
    if (dados?.destinatarioUsuarioId && dados.destinatarioUsuarioId !== usuario?.id) {
      return;
    }

    const notificacao = normalizarNotificacao(dados);

    setNotificacoes((atuais) => {
      const chaveNova = chaveNotificacao(notificacao);
      if (atuais.some((item) => chaveNotificacao(item) === chaveNova)) {
        return atuais;
      }
      return [notificacao, ...atuais].slice(0, 8);
    });
    setNaoLidas((total) => total + (notificacao.lida ? 0 : 1));
  }, [usuario?.id, usuario?.perfil]);

  const marcarComoLida = async (notificacao: Notificacao) => {
    try {
      await api.patch(`${endpointNotificacoes}/${notificacao.id}/lida`);
      setNotificacoes((atuais) => atuais.map((item) => item.id === notificacao.id ? { ...item, lida: true } : item));
      setNaoLidas((total) => Math.max(0, total - (notificacao.lida ? 0 : 1)));
    } catch (erro) {
      console.warn('Não foi possível marcar a notificação como lida.', erro);
    }
  };

  const marcarTodasComoLidas = async () => {
    try {
      await api.patch(`${endpointNotificacoes}/lidas`);
      setNotificacoes((atuais) => atuais.map((item) => ({ ...item, lida: true })));
      setNaoLidas(0);
    } catch (erro) {
      console.warn('Não foi possível marcar todas as notificações como lidas.', erro);
    }
  };

  const abrirNotificacao = async (notificacao: Notificacao) => {
    if (!notificacao.lida) {
      await marcarComoLida(notificacao);
    }
    if (notificacao.urlDestino) {
      navigate(notificacao.urlDestino);
    }
  };

  // Configuração de WebSocket
  useEffect(() => {
    const setupSocket = async () => {
      const token = await getToken();

      if (token && autenticado) {
        const socket = io(SOCKET_BASE_URL, {
          auth: { token }
        });

        socket.on('connect', () => {
          console.log('Conectado ao WebSocket');
        });

        notificationSocketEvents.forEach((evento) => socket.on(evento, adicionarNotificacao));

        socket.on('disconnect', () => {
          console.log('Desconectado do WebSocket');
        });

        return socket;
      }
    };

    let socketInstance: any;
    setupSocket().then(s => socketInstance = s);

    return () => {
      if (socketInstance) {
        notificationSocketEvents.forEach((evento) => socketInstance.off(evento, adicionarNotificacao));
        socketInstance.disconnect();
      }
    };
  }, [adicionarNotificacao, autenticado, tipoSessao]);

  useEffect(() => {
    if (autenticado) {
      carregarNotificacoes(1);
    } else {
      setNotificacoes([]);
      setNaoLidas(0);
      setPaginaNotificacoes(1);
      setTotalPaginasNotificacoes(1);
    }
  }, [autenticado, tipoSessao, carregarNotificacoes]);

  // Verifica autenticação ao montar
  useEffect(() => {
    const verificarAutenticacao = async () => {
      await carregarUsuarioSessao();
      const token = await storage.getItem('token_akzicao');
      const usuarioDados = await storage.getItem('usuario_akzicao');
      const tipo = (await storage.getItem(storageKeys.tipo) as SessaoTipo) || 'usuario';

      if (token && usuarioDados) {
        setAutenticado(true);
        setUsuario(JSON.parse(usuarioDados));
        setTipoSessao(tipo);
      }

      setCarregando(false);
    };

    verificarAutenticacao();
  }, []);

  const handleLogin = (dadosUsuario: any, tipo: SessaoTipo) => {
    setAutenticado(true);
    setUsuario(dadosUsuario);
    setTipoSessao(tipo);
    navigate(tipo === 'fornecedor' ? '/fornecedor' : perfilAdministrativo(dadosUsuario?.perfil) ? '/admin' : '/dashboard');
  };

  const handleLogout = async () => {
    api.post('/auth/logout').catch(() => undefined);
    limparSessao();
    setAutenticado(false);
    setUsuario(null);
    setTipoSessao('usuario');
    setNotificacoes([]);
    setNaoLidas(0);
    setNotificacoesAbertas(false);
    navigate('/login');
  };

  if (carregando) {
    return (
      <div className="loading-screen">
        <img className="loading-logo" src="/assets/logotipo-akzicao.png" alt="Akzição" />
        <div className="spinner"></div>
        <p>Inicializando Akzição...</p>
      </div>
    );
  }

  if (!autenticado) {
    return (
      <Routes>
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/delete-account" element={<DeleteAccount />} />
        <Route path="/login" element={<Login onLogin={handleLogin} />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  const menuInterno: Array<{ to: string; label: string; operacao: Operacao }> = [
    { to: '/dashboard', label: 'Dashboard', operacao: 'dashboard.visualizar' },
    { to: '/requisicoes', label: 'Requisições', operacao: 'requisicoes.visualizar' },
    { to: '/aprovacoes', label: 'Aprovações', operacao: 'aprovacoes.visualizar' },
    { to: '/comprador', label: 'Comprador', operacao: 'comprador.visualizar' },
    { to: '/armazem', label: 'Armazém', operacao: 'armazem.visualizar' },
    { to: '/fornecedores', label: 'Fornecedores', operacao: 'fornecedores.visualizar' },
    { to: '/equipa', label: 'Equipa interna', operacao: 'equipa.visualizar' },
    { to: '/subscricao', label: 'Subscrição', operacao: 'subscricao.visualizar' },
    { to: '/feedback', label: 'Feedback', operacao: 'feedback.visualizar' },
    { to: '/comunicacoes', label: 'Comunicações', operacao: 'comunicacoes.visualizar' },
    { to: '/admin', label: 'Admin global', operacao: 'admin.visualizar' }
  ];

  const menuFornecedor = [
    { to: '/fornecedor', label: 'Portal fornecedor' },
    { to: '/fornecedor/perfil', label: 'Configurações do perfil' },
    { to: '/comunicacoes', label: 'Comunicações' }
  ];

  const menuVisivel = tipoSessao === 'fornecedor'
    ? menuFornecedor
    : menuInterno.filter((item) => perfilPode(usuario?.perfil, item.operacao));

  const protegerRota = (operacao: Operacao, componente: React.ReactElement) =>
    perfilPode(usuario?.perfil, operacao) ? componente : <Navigate to={perfilAdministrativo(usuario?.perfil) ? '/admin' : '/dashboard'} replace />;

  const rotaPadrao = tipoSessao === 'fornecedor' ? '/fornecedor' : perfilAdministrativo(usuario?.perfil) ? '/admin' : '/dashboard';

  return (
    <div className="app-layout">
      {/* Sidebar */}
      <nav className="sidebar">
        <div className="sidebar-header">
          <div className="brand-small">
            <img src="/assets/logotipo-akzicao.png" alt="Akzição" />
            <h2>Akzição</h2>
          </div>
          <p className="tenant-nome">
            {tipoSessao === 'fornecedor'
              ? usuario?.nome_empresa
              : usuario?.empresa || usuario?.tenant?.nome_empresa}
          </p>
          {tipoSessao === 'usuario' && usuario?.tenant && (
            <div className="tenant-status-card">
              <span className="status-pill">{usuario.tenant.plano}</span>
              <span className={`status-pill ${['ATIVA', 'ATIVO'].includes(usuario.tenant.status) ? 'status-done' : 'status-blocked'}`}>
                {usuario.tenant.status}
              </span>
              {['EXPIRADO', 'EXPIRADA', 'SUSPENSO', 'SUSPENSA', 'CANCELADO', 'CANCELADA'].includes(usuario.tenant.status) && (
                <small>Ações operacionais serão bloqueadas pelo backend.</small>
              )}
            </div>
          )}
        </div>

        <ul className="menu">
          {menuVisivel.map((item) => (
            <li key={item.to}>
              <NavLink to={item.to} className={({ isActive }) => `menu-link ${isActive ? 'active' : ''}`}>
                {item.label}
              </NavLink>
            </li>
          ))}
          {tipoSessao === 'usuario' && usuario?.perfil === 'SUPER_ADMIN' && (
            <li><NavLink to="/admin/comunicados" className={({ isActive }) => `menu-link ${isActive ? 'active' : ''}`}>Comunicados</NavLink></li>
          )}
          {tipoSessao === 'usuario' && usuario?.perfil === 'GERENTE_EMPRESA' && (
            <li>
              <NavLink to="/configuracoes-empresa" className={({ isActive }) => `menu-link ${isActive ? 'active' : ''}`}>
                Configurações da empresa
              </NavLink>
            </li>
          )}
          <li><NavLink to="/sobre" className={({ isActive }) => `menu-link ${isActive ? 'active' : ''}`}>Sobre</NavLink></li>
        </ul>

        <div className="sidebar-footer">
          <div className="usuario-info">
            <p className="usuario-nome">{usuario?.nome}</p>
            <p className="usuario-perfil">{usuario?.perfil}</p>
          </div>
          <button className="botao botao-secundario" onClick={handleLogout}>
            Sair
          </button>
        </div>
      </nav>

      {/* Conteúdo Principal */}
      <div className="main-content">
        {tipoSessao === 'fornecedor' && <AlertaPerfilIncompleto tipo="fornecedor" />}
        {tipoSessao === 'usuario' && usuario?.perfil === 'GERENTE_EMPRESA' && <AlertaPerfilIncompleto tipo="tenant" />}

        <section className={`notificacoes-recentes ${notificacoesAbertas ? 'abertas' : ''}`} aria-label="Notificações">
          <div className="notificacoes-cabecalho">
            <button
              type="button"
              className="notificacoes-toggle"
              onClick={() => setNotificacoesAbertas((abertas) => !abertas)}
            >
              Notificações
              {naoLidas > 0 && <span>{naoLidas}</span>}
            </button>
            {notificacoesAbertas && (
              <button type="button" onClick={marcarTodasComoLidas} disabled={naoLidas === 0}>
                Marcar todas
              </button>
            )}
          </div>

          {notificacoesAbertas && (
            <>
              {carregandoNotificacoes && (
                <div className="notificacoes-vazio">A carregar notificações...</div>
              )}

              {erroNotificacoes && !carregandoNotificacoes && (
                <div className="notificacoes-vazio erro-notificacoes">
                  <span>{erroNotificacoes}</span>
                  <button type="button" className="botao-tentar-novamente" onClick={() => carregarNotificacoes(paginaNotificacoes)}>
                    Tentar novamente
                  </button>
                </div>
              )}

              {!carregandoNotificacoes && !erroNotificacoes && (
                <>
                  <ul>
                    {notificacoes.map((notificacao) => (
                      <li key={notificacao.id} className={notificacao.lida ? 'lida' : 'nao-lida'}>
                        <button type="button" onClick={() => abrirNotificacao(notificacao)}>
                          <strong>{notificacao.titulo || notificacao.tipo}</strong>
                          <span>{notificacao.mensagem}</span>
                          <small>{new Date(notificacao.createdAt).toLocaleString('pt-PT')}</small>
                        </button>
                        {!notificacao.lida && (
                          <button type="button" className="acao-lida" onClick={() => marcarComoLida(notificacao)}>
                            Lida
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>

                  {notificacoes.length === 0 && <div className="notificacoes-vazio">Sem notificações.</div>}

                  <div className="notificacoes-paginacao">
                    <button
                      type="button"
                      onClick={() => carregarNotificacoes(paginaNotificacoes - 1)}
                      disabled={paginaNotificacoes <= 1}
                    >
                      Anterior
                    </button>
                    <span>{paginaNotificacoes}/{totalPaginasNotificacoes}</span>
                    <button
                      type="button"
                      onClick={() => carregarNotificacoes(paginaNotificacoes + 1)}
                      disabled={paginaNotificacoes >= totalPaginasNotificacoes}
                    >
                      Seguinte
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </section>

        <Routes>
          <Route path="/dashboard" element={tipoSessao === 'usuario' ? protegerRota('dashboard.visualizar', <Dashboard />) : <Navigate to="/fornecedor" replace />} />
          <Route path="/requisicoes" element={protegerRota('requisicoes.visualizar', <Requisicoes />)} />
          <Route path="/aprovacoes" element={protegerRota('aprovacoes.visualizar', <Aprovacoes />)} />
          <Route path="/comprador" element={protegerRota('comprador.visualizar', <Comprador />)} />
          <Route path="/armazem" element={protegerRota('armazem.visualizar', <Armazem />)} />
          <Route path="/fornecedores" element={protegerRota('fornecedores.visualizar', <Fornecedores />)} />
          <Route path="/equipa" element={protegerRota('equipa.visualizar', <EquipaInterna />)} />
          <Route path="/subscricao" element={protegerRota('subscricao.visualizar', <SubscricaoEmpresa />)} />
          <Route path="/feedback" element={protegerRota('feedback.visualizar', <FeedbackPlataforma />)} />
          <Route path="/comunicacoes" element={tipoSessao === 'fornecedor' || perfilPode(usuario?.perfil, 'comunicacoes.visualizar') ? <Comunicacoes /> : <Navigate to={rotaPadrao} replace />} />
          <Route path="/comunicacoes/:id" element={tipoSessao === 'fornecedor' || perfilPode(usuario?.perfil, 'comunicacoes.visualizar') ? <Comunicacoes /> : <Navigate to={rotaPadrao} replace />} />
          <Route path="/fornecedor" element={tipoSessao === 'fornecedor' ? <FornecedorPortal /> : <Navigate to="/dashboard" replace />} />
          <Route path="/fornecedor/perfil" element={tipoSessao === 'fornecedor' ? <PerfilOperacional tipo="fornecedor" /> : <Navigate to="/dashboard" replace />} />
          <Route path="/configuracoes-empresa" element={tipoSessao === 'usuario' && usuario?.perfil === 'GERENTE_EMPRESA' ? <PerfilOperacional tipo="tenant" /> : <Navigate to="/dashboard" replace />} />
          <Route path="/admin" element={protegerRota('admin.visualizar', <AdminGlobal />)} />
          <Route path="/admin/comunicados" element={tipoSessao === 'usuario' && usuario?.perfil === 'SUPER_ADMIN' ? <AdminComunicados /> : <Navigate to={rotaPadrao} replace />} />
          <Route path="/sobre" element={<Sobre />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/delete-account" element={<DeleteAccount />} />
          <Route path="/login" element={<Navigate to={rotaPadrao} replace />} />
          <Route path="/" element={<Navigate to={rotaPadrao} replace />} />
          <Route path="*" element={<Navigate to={rotaPadrao} replace />} />
        </Routes>
        <footer className="app-footer">
          <span>Akzição · Estúdio Visão Ikigai · v{APP_VERSION}</span>
          <nav aria-label="Informação institucional">
            <NavLink to="/sobre">Sobre</NavLink>
            <NavLink to="/sobre#termos">Termos</NavLink>
            <NavLink to="/sobre#privacidade">Privacidade</NavLink>
            <NavLink to="/privacy-policy">Política de Privacidade</NavLink>
            <NavLink to="/delete-account">Exclusão de Conta</NavLink>
          </nav>
        </footer>
      </div>
    </div>
  );
};

const App: React.FC = () => (
  <DialogProvider>
    <DialogInitializer />
    <AppContent />
  </DialogProvider>
);

export default App;
